import { NetworkStatus } from "@apollo/client";
import { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import FormFooterEdit from "components/Forms/FooterEdit";
import StackEnableSensitiveOutputUploadFormField from "components/Forms/Stack/EnableSensitiveOutputUploadFormField";
import MissingDataBanner from "components/MissingDataBanner";
import { VENDOR_CONFIG_TYPENAME } from "constants/vendor";
import { WORKER_POOL_SHARED_VALUE } from "constants/workerPool";
import Badge from "ds/components/Badge";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import FeedbackActions from "ds/components/Feedback/Actions";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Select from "ds/components/Select";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageStack } from "hooks/useAnalytics/pages/stack";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import useUpdateStack from "shared/Stack/useUpdateStack";
import { useWorkerPoolOptions } from "shared/WorkerPool/useWorkerPoolOptions";
import { withoutPublicWorkerPool } from "utils/distribution";
import { stringIsRequired } from "utils/formValidators";

import { StackSettingsContextData } from "../Context";
import { REFETCH_STACK_AND_STACK_SETTINGS_QUERIES } from "../constants";
import {
  getAdministrativeTooltip,
  getAllowRunPromotionTooltip,
  getAutodeployTooltip,
  getAutoretryTooltip,
  getEnableSecretMaskingTooltip,
  getLocalPreviewEnabledTooltip,
  getProtectFromDeletionTooltip,
  getRunnerImageTooltip,
  getWorkerPoolTooltip,
} from "./getTooltips";
import { getFormDefaultValues, mapCreateStackBehaviorUpdateInput } from "./helpers";

const noPublicWorkerPool = withoutPublicWorkerPool();

const ADMINISTRATIVE_DEPRECATED_BANNER_STORAGE_KEY =
  "stack-settings-administrative-deprecated-banner";

export type StackSettingsBehaviorFormFields = {
  workerPool: string;
  runnerImage: string;
  administrative: boolean;
  githubActionDeploy: boolean;
  autodeploy: boolean;
  autoretry: boolean;
  protectFromDeletion: boolean;
  localPreviewEnabled: boolean;
  enableWellKnownSecretMasking: boolean;
  enableSensitiveOutputUpload: boolean;
};

const StackSettingsBehaviorEdit = () => {
  const { stackSettings } = useTypedContext(StackSettingsContextData);
  const { disableAdministrativeStacks } = useTypedFlags();
  const isPulumiVendor = stackSettings.vendorConfig?.__typename === VENDOR_CONFIG_TYPENAME.PULUMI;

  const trackSegmentEvent = useAnalytics({
    page: AnalyticsPageStack.StackSettingsBehavior,
  });

  const {
    workerPoolsOptions,
    hasData: hasWorkerPoolsOptions,
    refetch: refetchWorkerPoolsOptions,
    loading: workerPoolsOptionsLoading,
    networkStatus: workerPoolsOptionsNetworkStatus,
  } = useWorkerPoolOptions(stackSettings.spaceDetails.id);

  const { stackUpdate, loading } = useUpdateStack({
    stack: stackSettings,
    refetchQueries: REFETCH_STACK_AND_STACK_SETTINGS_QUERIES,
  });

  const stackBehaviorForm = useForm<StackSettingsBehaviorFormFields>({
    defaultValues: getFormDefaultValues(stackSettings),
    mode: "onChange",
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    setValue,
    watch,
  } = stackBehaviorForm;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const selectedWorkerPool = watch("workerPool");

  const handleWorkerPoolChange = (workerPool: string) => {
    setValue("workerPool", workerPool, { shouldValidate: true, shouldDirty: true });

    if (workerPool === WORKER_POOL_SHARED_VALUE) {
      setValue("autoretry", false);
    }
  };

  const onSubmit = (formData: StackSettingsBehaviorFormFields) => {
    stackUpdate(mapCreateStackBehaviorUpdateInput(formData));
  };

  useEffect(() => {
    reset(getFormDefaultValues(stackSettings));
  }, [reset, stackSettings]);

  useEffect(() => {
    if (!selectedWorkerPool) {
      if (noPublicWorkerPool && workerPoolsOptions?.length) {
        setValue("workerPool", workerPoolsOptions[0].value, { shouldValidate: true });
      }
    } else if (hasWorkerPoolsOptions || (!workerPoolsOptionsLoading && !hasWorkerPoolsOptions)) {
      // validate worker pool shared via link
      const isNotAvailable = !workerPoolsOptions.find(({ value }) => {
        return value === selectedWorkerPool;
      });
      if (isNotAvailable) {
        setValue("workerPool", "");
      }
    }
  }, [
    workerPoolsOptions,
    setValue,
    selectedWorkerPool,
    hasWorkerPoolsOptions,
    workerPoolsOptionsLoading,
  ]);

  return (
    <FormProvider {...stackBehaviorForm}>
      <Controller
        name="workerPool"
        rules={{
          required: "Worker pool is required",
        }}
        control={control}
        render={({ field, fieldState }) => (
          <FormField
            error={fieldState.error?.message}
            label="Worker pool"
            {...getWorkerPoolTooltip()}
            noMargin
          >
            {({ ariaInputProps }) => (
              <>
                <Select
                  isDisabled={workerPoolsOptions.length === 1}
                  value={field.value}
                  items={workerPoolsOptions}
                  onChange={handleWorkerPoolChange}
                  isError={!!fieldState.error}
                  placeholder="Select worker pool"
                  {...ariaInputProps}
                />
                {!hasWorkerPoolsOptions && !workerPoolsOptionsLoading && (
                  <Box direction="column" margin="large 0 0 0">
                    <MissingDataBanner
                      text="Couldn't load your private worker pools, please refresh or come back later"
                      refreshHandler={refetchWorkerPoolsOptions}
                      refreshLoading={
                        workerPoolsOptionsLoading &&
                        workerPoolsOptionsNetworkStatus === NetworkStatus.refetch
                      }
                    />
                  </Box>
                )}
              </>
            )}
          </FormField>
        )}
      />
      <FormField
        label="Runner image"
        error={errors?.runnerImage?.message}
        isOptional={!isPulumiVendor}
        noMargin
        {...getRunnerImageTooltip()}
      >
        {({ ariaInputProps }) => (
          <Input
            placeholder="Runner image"
            {...register("runnerImage", {
              setValueAs: (value: string) => value.trim(),
              ...(isPulumiVendor && { validate: stringIsRequired() }),
            })}
            error={!!errors?.runnerImage?.message}
            {...ariaInputProps}
          />
        )}
      </FormField>
      {!disableAdministrativeStacks && (
        <Controller
          name="administrative"
          control={control}
          render={({ field }) => (
            <Box direction="column" gap="medium">
              <FormToggleField
                variant="switch"
                onChange={(value) => {
                  field.onChange(value);
                  trackSegmentEvent("Administrative toggled", { enabled: value });
                }}
                checked={field.value}
                title="Administrative"
                titleSuffix={<Badge variant="red" type="strong" text="DEPRECATED" />}
                description="Stack will receive a runtime environment variable giving administrative access to other stacks within the same account"
                tooltipInfo={getAdministrativeTooltip().tooltipInfo}
              />
              <Banner
                variant="warning"
                title="Feature is deprecated"
                storageKey={ADMINISTRATIVE_DEPRECATED_BANNER_STORAGE_KEY}
              >
                This feature is deprecated and is being replaced by role-based access. When both are
                used, the administrative flag takes precedence. To rely solely on role assignments,
                toggle off administrative access.
                <FeedbackActions>
                  <LinkButton size="small" to="../roles" variant="contrast">
                    Assign roles
                  </LinkButton>
                </FeedbackActions>
              </Banner>
            </Box>
          )}
        />
      )}
      <Controller
        name="githubActionDeploy"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            onChange={field.onChange}
            checked={field.value}
            title="Allow run promotion"
            description="Proposed runs can be promoted (deployed) using the Spacelift API or GitHub Actions"
            tooltipInfo={getAllowRunPromotionTooltip().tooltipInfo}
          />
        )}
      />
      <Controller
        name="autodeploy"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            onChange={field.onChange}
            checked={field.value}
            title="Autodeploy"
            description="Changes to the stack will be applied automatically"
            tooltipInfo={getAutodeployTooltip().tooltipInfo}
          />
        )}
      />
      <Controller
        name="autoretry"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            disabled={selectedWorkerPool === WORKER_POOL_SHARED_VALUE}
            onChange={field.onChange}
            checked={field.value}
            title="Autoretry"
            description="Tracked runs will automatically retry invalidated proposed runs"
            tooltipInfo={getAutoretryTooltip().tooltipInfo}
          />
        )}
      />
      <Controller
        name="localPreviewEnabled"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            onChange={field.onChange}
            checked={field.value}
            title="Enable local preview"
            description="Run local previews using spacectl."
            tooltipInfo={getLocalPreviewEnabledTooltip().tooltipInfo}
          />
        )}
      />
      <Controller
        name="enableWellKnownSecretMasking"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            onChange={field.onChange}
            checked={field.value}
            title="Enable secret masking"
            description="Indicates whether secret patterns will be automatically redacted from logs."
            tooltipInfo={getEnableSecretMaskingTooltip().tooltipInfo}
          />
        )}
      />
      <Controller
        name="protectFromDeletion"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            onChange={field.onChange}
            checked={field.value}
            title="Protect from deletion (recommended)"
            description="Manage deletion protection"
            tooltipInfo={getProtectFromDeletionTooltip().tooltipInfo}
          />
        )}
      />
      <StackEnableSensitiveOutputUploadFormField<StackSettingsBehaviorFormFields> name="enableSensitiveOutputUpload" />
      <FormFooterEdit
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => reset(getFormDefaultValues(stackSettings))}
        isDirty={isDirty}
        submitLoading={loading}
      />
    </FormProvider>
  );
};

export default StackSettingsBehaviorEdit;
